import {
  dateArrayToDate,
  type Environment,
} from '@openeducation/oe-it-services';
import type { CurrentClass } from '@openeducation/oe-it-shared-types';

import ActiveClassesDao from './dao/active-classes.dao';
import {
  ActiveClassCurrentClass,
  ActiveClass,
} from '../interfaces/active-class';

export abstract class ActiveClasses {
  static getActiveClasses(
    env: Environment,
    teacherId?: number
  ): Promise<ActiveClassCurrentClass> {
    if (!teacherId) {
      return Promise.reject(new Error('Missing teacherId'));
    }
    return ActiveClassesDao.getActiveClasses(env, teacherId);
  }
  static async getActiveClassStatus(env: Environment) {
    try {
      await ActiveClassesDao.getActiveClassStatus(env);

      return false;
    } catch {
      return true;
    }
  }
  static async disableLiveClasses(env: Environment, teacherId?: number) {
    if (!teacherId) {
      return Promise.reject(new Error('Missing teacherId'));
    }
    return ActiveClassesDao.disableLiveClasses(env, teacherId);
  }
  static async enableLiveClasses(env: Environment, teacherId?: number) {
    if (!teacherId) {
      return Promise.reject(new Error('Missing teacherId'));
    }
    return ActiveClassesDao.enableLiveClasses(env, teacherId);
  }
  static async reassignTeacher(
    env: Environment,
    originalTeacherId: number,
    classroomId: number,
    teacherId?: number
  ) {
    if (!teacherId) {
      return Promise.reject(new Error('Missing teacherId'));
    }
    if (!originalTeacherId) {
      return Promise.reject(new Error('Missing originalTeacherId'));
    }
    if (!classroomId) {
      return Promise.reject(new Error('Missing classroomId'));
    }
    return ActiveClassesDao.reassignTeacher(
      env,
      teacherId,
      originalTeacherId,
      classroomId
    );
  }

  static async getRejoinClasses(
    env: Environment,
    teacherId?: number
  ): Promise<ActiveClass[]> {
    if (!teacherId) {
      return Promise.reject(new Error('Missing teacherId'));
    }

    const classes = await ActiveClassesDao.getRejoinClasses(env, teacherId);

    return this.filterRejoinClasses(classes);
  }

  private static hasMultipleRejoinClasses(classes: ActiveClass[]) {
    return classes.length > 1;
  }

  private static filterRejoinClasses(classes: ActiveClass[]) {
    if (!this.hasMultipleRejoinClasses(classes)) {
      return classes;
    }

    return [...classes]
      .sort((a, b) => {
        const startA = dateArrayToDate(a.classBegin)!.getTime();
        const startB = dateArrayToDate(b.classBegin)!.getTime();
        return startB - startA;
      })
      .slice(0, 1);
  }

  static getCurrentClass(
    env: Environment,
    classroomId?: number
  ): Promise<CurrentClass> {
    if (!classroomId) {
      return Promise.reject(new Error('Missing classroomId'));
    }

    return ActiveClassesDao.getCurrentClass(env, classroomId);
  }

  static async updateClassStatus(
    env: Environment,
    classroomId?: string,
    status?: string
  ) {
    if (!status) {
      return Promise.reject(new Error('Missing status'));
    }
    if (!classroomId) {
      return Promise.reject(new Error('Missing classroomId'));
    }
    return ActiveClassesDao.updateClassStatus(env, classroomId, status);
  }
}
